import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { dashboardApi } from '../api';
import { FiServer, FiActivity, FiDollarSign, FiCheckCircle, FiPauseCircle, FiClock, FiTrendingUp, FiUsers, FiAlertCircle, FiLoader } from 'react-icons/fi';
import toast from 'react-hot-toast';

const statusConfig = {
  active:    { label: 'Active',    icon: FiCheckCircle, color: 'bg-green-500',   bg: 'bg-green-50 dark:bg-green-900/20', txt: 'text-green-700 dark:text-green-400' },
  suspended: { label: 'Suspended', icon: FiPauseCircle, color: 'bg-orange-500',  bg: 'bg-orange-50 dark:bg-orange-900/20', txt: 'text-orange-700 dark:text-orange-400' },
  pending:   { label: 'Pending',   icon: FiClock,       color: 'bg-yellow-500',  bg: 'bg-yellow-50 dark:bg-yellow-900/20', txt: 'text-yellow-700 dark:text-yellow-400' },
  deploying: { label: 'Deploying', icon: FiLoader,      color: 'bg-blue-500',    bg: 'bg-blue-50 dark:bg-blue-900/20', txt: 'text-blue-700 dark:text-blue-400' },
  inactive:  { label: 'Inactive',  icon: FiAlertCircle, color: 'bg-red-500',     bg: 'bg-red-50 dark:bg-red-900/20', txt: 'text-red-700 dark:text-red-400' },
};

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await dashboardApi.getStats();
        setStats(data);
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load stats';
        toast.error(msg);
        setStats({ total: 0, active: 0, suspended: 0, pending: 0, deploying: 0, inactive: 0, totalRevenue: 0, totalOrders: 0, totalUsers: 0, recentActivity: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statusKeys = ['active', 'suspended', 'pending', 'deploying', 'inactive'];

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {stats.total} panel{stats.total !== 1 ? 's' : ''} · {stats.totalOrders?.toLocaleString()} orders · {stats.totalUsers?.toLocaleString()} users
        </p>
      </div>

      {/* Status Distribution Bar */}
      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground tracking-tight">Panel Status</h3>
          <button onClick={() => navigate('/panels')} className="text-xs text-primary hover:underline font-medium">View All</button>
        </div>
        <div className="flex h-8 rounded-lg overflow-hidden border border-border">
          {statusKeys.map(key => {
            const count = stats[key] || 0;
            if (count === 0) return null;
            const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={key} title={`${statusConfig[key].label}: ${count}`}
                className={`${statusConfig[key].color} flex items-center justify-center text-[10px] font-bold text-white transition-all hover:opacity-90`}
                style={{ width: `${pct}%`, minWidth: count > 0 ? '20px' : '0' }}>
                {pct > 8 ? `${count}` : ''}
              </div>
            );
          })}
          {stats.total === 0 && <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground bg-muted/30">No panels</div>}
        </div>
        <div className="flex flex-wrap gap-4 mt-3">
          {statusKeys.map(key => {
            const count = stats[key] || 0;
            const cfg = statusConfig[key];
            if (count === 0) return null;
            return (
              <div key={key} className="flex items-center gap-1.5 text-xs">
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
                <span className="text-muted-foreground">{cfg.label}</span>
                <span className="font-semibold text-foreground">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Panels</div>
              <div className="text-3xl font-bold text-foreground mt-1">{stats.total}</div>
            </div>
            <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
              <FiServer size={20} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FiTrendingUp size={12} className="text-green-500" />
            <span>{stats.active} active</span>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Orders</div>
              <div className="text-3xl font-bold text-foreground mt-1">{stats.totalOrders?.toLocaleString()}</div>
            </div>
            <div className="p-2.5 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
              <FiTrendingUp size={20} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            across {stats.total} panels
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Revenue</div>
              <div className="text-3xl font-bold text-foreground mt-1">${Number(stats.totalRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
              <FiDollarSign size={20} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FiUsers size={12} /> {stats.totalUsers?.toLocaleString()} users
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status Breakdown</div>
              <div className="mt-1 space-y-1">
                {statusKeys.map(key => {
                  const count = stats[key] || 0;
                  if (count === 0) return null;
                  const cfg = statusConfig[key];
                  return (
                    <div key={key} className="flex items-center gap-1.5 text-xs">
                      <span className={`w-2 h-2 rounded-full ${cfg.color}`} />
                      <span className="text-muted-foreground w-16">{cfg.label}</span>
                      <span className="font-semibold text-foreground">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-border">
          <h2 className="text-base font-semibold text-foreground tracking-tight">Recent Activity</h2>
        </div>
        <div className="overflow-x-auto">
          {stats?.recentActivity?.length > 0 ? (
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Action</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Panel</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Time</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {stats.recentActivity.map((act, i) => (
                  <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                        act.action.includes('CREATED') ? 'bg-secondary text-secondary-foreground border-transparent' :
                        act.action.includes('DELETE') || act.action.includes('DISABLED') ? 'bg-destructive/10 text-destructive border-transparent' :
                        'bg-muted text-foreground border-border'
                      }`}>
                        {act.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-4 align-middle font-medium text-foreground">{act.panel}</td>
                    <td className="p-4 align-middle text-muted-foreground text-right">
                      <div className="flex items-center justify-end gap-2">
                        <FiClock size={14} className="shrink-0" />
                        <span title={new Date(act.time).toLocaleString()}>{formatTime(act.time)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-10 text-center text-muted-foreground flex flex-col items-center">
              <FiActivity size={32} className="mb-3 opacity-20" />
              <p className="text-sm">No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
